import { type FC, type PropsWithChildren, type ReactNode, useEffect, useId, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import classNames from 'classnames'
import { RoomSessionList } from '../../../apps/professional-app/fa/PastMeetings/PastMeetings'
import { DEFAULT_URL_PAGE_SIZE, type UrlPagination } from './useUrlPagination'
import { CustomPaginationComponent } from '../../../components/DataTable/CustomPaginationComponent'
import { type RoomSession } from '../../../models/Room'
import { type BasePageResponse } from '../../../store/models'
import { UpcomingRoomSessionList } from '../../Upcoming/Upcoming'
import { useContactPastMeetings, useContactUpcomingMeetings } from './hooks'

const ERROR_MESSAGE = 'Error loading meetings!'

interface ContactMeetingsProps {
  contactId: string
}

export const ContactMeetings: FC<ContactMeetingsProps> = ({ contactId }) => {
  return (
    <div key={contactId} className='d-flex flex-column gap-4'>
      <ContactUpcomingMeetings contactId={contactId} />
      <ContactPastMeetings contactId={contactId} />
    </div>
  )
}

const ContactUpcomingMeetings: FC<ContactMeetingsProps> = ({ contactId }) => {
  const { currentData, isFetching, isError, refetch, pagination } = useContactUpcomingMeetings(contactId)

  return (
    <ContactMeetingsList
      title='Upcoming meetings'
      data={currentData}
      isFetching={isFetching}
      isError={isError}
      emptyMessage={<ContactMeetingsEmptyState icon='bi-calendar'>No upcoming meeting scheduled with this contact.</ContactMeetingsEmptyState>}
      renderRows={(sessions) => <UpcomingRoomSessionList roomSessions={sessions} onSessionChange={refetch} showPluginPromo={false} />}
      pagination={pagination}
    />
  )
}

const ContactPastMeetings: FC<ContactMeetingsProps> = ({ contactId }) => {
  const { currentData, isFetching, isError, pagination } = useContactPastMeetings(contactId)

  return (
    <ContactMeetingsList
      title='Past meetings'
      data={currentData}
      isFetching={isFetching}
      isError={isError}
      emptyMessage={
        <ContactMeetingsEmptyState icon='bi-clock-history'>
          No past meetings were found for the contact. Make sure to assign the contacts for the meetings.
        </ContactMeetingsEmptyState>
      }
      renderRows={(sessions) => <RoomSessionList roomSessions={sessions} showDemoSession={false} />}
      pagination={pagination}
    />
  )
}

const ContactMeetingsEmptyState: FC<PropsWithChildren<{ icon: string }>> = ({ icon, children }) => (
  <div className='d-flex align-items-center gap-2 p-3 border zocks-border-dashed zocks-border-color-gray-400 rounded-3 paragraph-medium zocks-text-gray-700'>
    <i className={classNames('bi', icon)} aria-hidden='true' />
    {children}
  </div>
)

interface ContactMeetingsListProps {
  title: string
  data: BasePageResponse<RoomSession[]> | undefined
  isFetching: boolean
  isError: boolean
  emptyMessage: ReactNode
  renderRows: (sessions: RoomSession[]) => ReactNode
  pagination: UrlPagination
}

const ContactMeetingsList: FC<ContactMeetingsListProps> = ({ title, data, isFetching, isError, emptyMessage, renderRows, pagination }) => {
  const isLoading = isFetching && !data
  const total = data?.page.totalElements
  const showCount = !isError && total !== undefined

  const { page, pageSize, setPage } = pagination
  const lastPage = Math.max(1, Math.ceil((total ?? 0) / pageSize))
  const isPastLastPage = !isError && total !== undefined && page > lastPage

  useEffect(() => {
    if (isPastLastPage) {
      setPage(lastPage)
    }
  }, [isPastLastPage, lastPage, setPage])

  const getBody = () => {
    if (isLoading || isPastLastPage) {
      return (
        <div className='d-flex justify-content-center'>
          <Spinner />
        </div>
      )
    }
    if (isError) {
      return <div>{ERROR_MESSAGE}</div>
    }
    if (!data?.data.length || !total) {
      return emptyMessage
    }
    return (
      <>
        {renderRows(data.data)}
        {total > DEFAULT_URL_PAGE_SIZE ? (
          <CustomPaginationComponent
            rowsPerPage={pageSize}
            rowCount={total}
            currentPage={page}
            onChangePage={setPage}
            onChangeRowsPerPage={pagination.setPageSize}
          />
        ) : null}
      </>
    )
  }

  return <ContactMeetingsSection title={showCount ? `${title} (${total})` : title}>{getBody()}</ContactMeetingsSection>
}

const ContactMeetingsSection: FC<PropsWithChildren<{ title: string }>> = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(true)
  const bodyId = useId()

  return (
    <section className='d-flex flex-column gap-3 pb-4 border-bottom zocks-border-color-gray-400'>
      <button
        type='button'
        className='d-flex align-items-center justify-content-between w-100 px-0 py-2 border-0 bg-transparent text-start zocks-text-gray-900'
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className='heading-small'>{title}</span>
        <i className={classNames('bi', { 'bi-chevron-up': isOpen, 'bi-chevron-down': !isOpen })} aria-hidden='true' />
      </button>
      {isOpen ? <div id={bodyId}>{children}</div> : null}
    </section>
  )
}
